import React from "react";
import { Link } from "react-router-dom";
import * as CHQ from "chq";

import { ProfileLink } from "app/links";
import { select, Trans } from "app/utils";

import getText from "components/editor/getText";

import NotificationBody from "./NotificationBody";

const getMentionPath = (mention: CHQ.Mention) => {
  switch (mention.entityType) {
    case "Announcement":
      return `/featured/${mention.entity.id}`;
    case "Comment":
      if (mention.entity.parentCommentId) {
        return `/comments/${mention.entity.parentCommentId}`;
      }
      return `/comments/${mention.entity.id}`;
    case "Event":
      return `/events/${mention.entity.id}`;
    case "Post":
      return `/posts/${mention.entity.id}`;
    case "Recognition":
      return `/recognitions/${mention.entity.id}`;
    case "Story":
      return `/stories/trend/${mention.entity.question.id}/story/${mention.entity.id}`;
    default:
      // Technically we're in a `never` case here, but we want to preserve this
      // in case we're adding a new mention type and the frontend on the client
      // hasn't caught up to the backend.
      return "/";
  }
};

export const getMentionText = (mention: CHQ.Mention): null | CHQ.RichText => {
  switch (mention.entityType) {
    case "Announcement":
      return mention.entity.body;
    case "Comment":
      return mention.entity.body;
    case "Event":
      return mention.entity.details;
    case "Post":
      return mention.entity.body;
    case "Recognition":
      return mention.entity.body;
    case "Story":
      return mention.entity.body;
    default:
      return null;
  }
};

type MentionedNotificationProps = {
  notification: CHQ.NotificationMentioned;
  tabIndex?: number;
};

const MentionedNotification: React.FC<MentionedNotificationProps> = ({
  notification: { actor, entity }, tabIndex
}) => (
  <NotificationBody actor={actor}>
    <Trans>
      <ProfileLink user={actor} tabIndex={tabIndex || 0} />
      {" "}
      mentioned you in
      {" "}
      <Link to={getMentionPath(entity)} tabIndex={tabIndex || 0}>
        {select(entity.entityType, {
          Announcement: "featured content",
          Comment: "a comment",
          Event: "an event",
          Post: "an update",
          Recognition: "a recognition",
          Story: "a story"
        })}
      </Link>
      :
    </Trans>
    {" "}
    {getText(getMentionText(entity))}
  </NotificationBody>
);

export default MentionedNotification;
